<script>
import { mapMutations } from "vuex"
import { useTransformationFunctionsStore } from "@/store/modules/transformationFunctions"
import ErrorModal from "@/components/confirmations/error.vue"
import TransformationFunctionDefinition from "@/components/transformation-functions/form-parts/definition.vue"
import TransformationFunctionCode from "@/components/transformation-functions/form-parts/code.vue"
import TransformationFunctionTesting from "@/components/transformation-functions/form-parts/testing.vue"
import TransformationFunctionUsage from "@/components/transformation-functions/form-parts/usage.vue"

export default {
  name: "TransformationFunctionFormModal",
  components: {
    TransformationFunctionUsage,
    TransformationFunctionTesting,
    TransformationFunctionCode,
    TransformationFunctionDefinition,
    ErrorModal
  },
  emits: ["change", "close"],
  props: {
    transformationFunction: {
      type: Object,
      required: true
    },
    isOpened: {
      type: Boolean,
      required: true
    },
    readOnly: {
      type: Boolean,
      required: false,
      default: false
    }
  },
  setup() {
    const tfStore = useTransformationFunctionsStore()
    return { tfStore }
  },
  data() {
    return {
      isErrorOpened: false,
      errors: [],
      transformationDefinitionTypeOptions: [
        { text: "Groovy", value: "GROOVY" },
        { text: "Javascript", value: "JAVASCRIPT" }
      ],
      selectedTab: 1
    }
  },
  watch: {
    isOpened() {
      this.selectedTab = 1
      if (this.isOpened && !this.readOnly && !this.transformationFunction.definitionType) {
        this.transformationFunction.definitionType = this.transformationDefinitionTypeOptions[0].value
      }
    }
  },
  computed: {
    isFormValid() {
      return !!(
        this.transformationFunction.label &&
        this.transformationFunction.type &&
        this.transformationFunction.subType &&
        this.transformationFunction.definition
      )
    }
  },
  methods: {
    ...mapMutations({
      setNotification: "modals/setGlobalNotification",
      setWaitingLoader: "modals/setWaitingLoader"
    }),
    setTransformationFunction(data) {
      this.tfStore.setDetail(data)
    },
    createTransformationFunction() {
      return this.tfStore.create()
    },
    updateTransformationFunction() {
      return this.tfStore.update()
    },
    async handleSave(shouldClose = true) {
      this.setWaitingLoader(this.$t("SAVING"))
      try {
        this.setTransformationFunction(this.transformationFunction)
        if (this.transformationFunction.transformationId) {
          await this.updateTransformationFunction()
        } else {
          await this.createTransformationFunction()
        }
        const notification = [
          {
            text: "TRANSFORMATION_FUNCTION_SAVED",
            error: false,
            params: {
              transformationLabel: this.transformationFunction.label
            }
          }
        ]
        this.setNotification(notification)
        this.$emit("change")
        if (shouldClose) {
          this.$emit("close")
        }
      } catch (e) {
        this.errors = e.data.errors
        this.isErrorOpened = true
      }
      this.setWaitingLoader("")
    }
  }
}
</script>

<template>
  <div :class="{ active: isOpened }" class="side-box">
    <span class="side-box__overlay" />
    <div
      :class="{
        opened: isOpened,
        'last-active': true
      }"
      :style="isOpened ? '' : 'right: -1500px'"
      class="side-box__inner"
      style="width: 1500px"
    >
      <div class="side-box__header d-flex justify-content-between align-items-center">
        <div class="atom-back atom-back--gray" @click="$emit('close')">
          <div class="atom-back__inner d-flex align-items-center">
            <img src="@/assets/icons/arrows/arr-gray-left.svg" />
            <span>
              {{ $t("zpet") }}
            </span>
          </div>
        </div>
        <div class="d-flex gap-2">
          <button
            v-if="!readOnly && transformationFunction.transformationId"
            :class="isFormValid ? '' : 'disabled'"
            class="btn btn-primary primary-shadow"
            @click="handleSave(false)"
          >
            <div class="atom-icon">
              <img src="@/assets/icons/actions/ico-btn-ulozit.svg" />
            </div>
            <span>
              {{ $t("SAVE") }}
            </span>
          </button>
          <button
            v-if="!readOnly"
            :class="isFormValid ? '' : 'disabled'"
            class="btn btn-primary primary-shadow"
            @click="handleSave"
          >
            <div class="atom-icon">
              <img src="@/assets/icons/actions/ico-btn-ulozit.svg" />
            </div>
            <span v-if="transformationFunction.transformationId">
              {{ $t("FINISH_AND_SAVE") }}
            </span>
            <span v-else>
              {{ $t("CREATE") }}
            </span>
          </button>
        </div>
      </div>
      <div class="side-box__title">
        <h2>{{ $t("TRANSFORMATION_FUNCTION") }}</h2>
      </div>
      <div class="side-box__content">
        <div id="tabs" class="detail-page__tabs">
          <div class="detail-page__tabs__header d-flex">
            <div class="d-flex align-items-end detail-page__tabs__wrapper">
              <div :class="{ active: selectedTab === 1 }" class="detail-page__tabs__tab" @click="selectedTab = 1">
                {{ $t("DEFINITION") }}
              </div>
              <div :class="{ active: selectedTab === 2 }" class="detail-page__tabs__tab" @click="selectedTab = 2">
                {{ $t("SOURCE_CODE") }}
              </div>
              <div
                v-if="transformationFunction.transformationId"
                :class="{ active: selectedTab === 3 }"
                class="detail-page__tabs__tab"
                @click="selectedTab = 3"
              >
                {{ $t("TESTING") }}
              </div>
              <div
                v-if="transformationFunction.transformationId"
                :class="{ active: selectedTab === 4 }"
                class="detail-page__tabs__tab"
                @click="selectedTab = 4"
              >
                {{ $t("USAGE") }}
              </div>
            </div>
          </div>
          <div class="detail-page__tabs__content overflow-visible pb-4">
            <div v-if="selectedTab === 1">
              <TransformationFunctionDefinition
                :read-only="readOnly"
                :transformation-function="transformationFunction"
              />
            </div>
            <div v-if="selectedTab === 2">
              <TransformationFunctionCode :read-only="readOnly" :transformation-function="transformationFunction" />
            </div>
            <div v-show="selectedTab === 3">
              <TransformationFunctionTesting :transformation-function="transformationFunction" />
            </div>
            <div v-if="selectedTab === 4">
              <TransformationFunctionUsage :transformation-function="transformationFunction" />
            </div>
          </div>
        </div>
      </div>
    </div>
    <ErrorModal :centered="true" :errors="errors" :opened="isErrorOpened" @close="isErrorOpened = false" />
  </div>
</template>
